import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';

import type { TableRef } from 'af-components/Table';
import Table from 'af-components/Table';
import DollarCell from 'af-components/Table/Cells/DollarCell';
import type { TableProps } from 'af-components/Table/types';

import styles from './styles.module.scss';
import type { EquipmentUtilizationTableBaseVM } from './types';
import WorkingCell from '../../UtilizationShared/WorkingCellNew';

interface OwnProps {
	tableName: string;
	label: string;
	goDownInSelection: (groupingId: string, timePeriodId: string) => void;
	hasSearchInput: TableProps<EquipmentUtilizationTableBaseVM>['hasSearchInput'];
	fetch: TableProps<EquipmentUtilizationTableBaseVM>['fetch'];
	buttons?: TableProps<EquipmentUtilizationTableBaseVM>['buttons'];
	additionalFilter?: TableProps<EquipmentUtilizationTableBaseVM>['additionalFilter'];
	tableRef: React.RefObject<Nullable<TableRef<EquipmentUtilizationTableBaseVM>>>;
}

type Props = OwnProps;

const _renderDollarAmountCell = () => (cell: CellContext<EquipmentUtilizationTableBaseVM, number>) => {
	return (
		<DollarCell
			className={styles['revenue-cell']}
			isUnitPrice={true}
			maxFractionDigits={2}
			value={cell.getValue()}
		/>
	);
};

const _renderWorkingCell = (cell: CellContext<EquipmentUtilizationTableBaseVM, {
	daysAssigned: number;
	daysAvailable: number;
	daysUnavailable: number;
	totalDays: number;
}>) => {
	return (
		<WorkingCell
			daysAssigned={cell.row.original.daysAssigned}
			daysAvailable={cell.row.original.daysAvailable}
			daysUnavailable={cell.row.original.daysUnavailable}
			totalDays={cell.row.original.totalDays}
		/>
	);
};

const EquipmentUtilizationGroupTable: React.FC<Props> = (props) => {
	const { tableName, label, goDownInSelection, hasSearchInput, fetch, buttons, additionalFilter, tableRef } = props;

	const columns: TableProps<EquipmentUtilizationTableBaseVM>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			header: label,
			accessor: 'name',
			enableSorting: true,
			cell: (cell: CellContext<EquipmentUtilizationTableBaseVM, string>) => (
				<span>
					<strong>{cell.getValue()}</strong><span className="text-grey m-l-s">{cell.row.original.equipmentCount}</span>
				</span>
			),
		},
		{
			id: 'timePeriod',
			header: 'Year',
			accessor: 'timePeriod',
			enableSorting: true,
			cell: (cell) => cell.getValue() ?? null,
		},
		{
			id: 'daysAssigned',
			header: 'Working',
			accessor: 'daysAssigned',
			enableSorting: true,
			cell: _renderWorkingCell,
		},
		{
			id: 'dailyRevenue',
			header: 'Daily Revenue',
			accessor: 'dailyRevenue',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'dailyCost',
			header: 'Daily Cost',
			accessor: 'dailyCost',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
		{
			id: 'targetProfit',
			header: 'Target Profit/Loss',
			accessor: 'targetProfit',
			enableSorting: true,
			cell: _renderDollarAmountCell(),
		},
	], [label]);

	const onRowClick = React.useCallback((row: Row<EquipmentUtilizationTableBaseVM>) => {
		goDownInSelection(row.original.groupId, row.original.timePeriod);
	}, [goDownInSelection]);

	return (
		<Table
			additionalFilter={additionalFilter}
			buttons={buttons}
			columns={columns}
			fetch={fetch}
			hasSearchInput={hasSearchInput}
			hasSubRows={false}
			onRowClick={onRowClick}
			ref={tableRef}
			searchLabel={label}
			selectable={true}
			tableName={tableName}
		/>
	);
};

export default React.memo(EquipmentUtilizationGroupTable);
